import type { CSSProperties } from 'react';
import { z } from 'zod';
import { MainTitle, TextBloc } from '@/components/TextBlock';
import { TitleColumn } from '@/components/TitleColumn';
import { widgetLayoutSchema } from '@/config/schema';
import { useWidgetData } from '@/hooks/useWidgetData';
import { useWidgetLayout } from '@/hooks/useWidgetLayout';
import { fontOptionsSchema, fontStyleToCss } from '@/lib/fontStyle';
import { parseStyleString } from '@/lib/parseStyleString';
import { placeholderText } from '@/lib/text';
import { collapsesTitleColumn } from '@/lib/titleColumn';
import { warnOnce } from '@/lib/warnOnce';
import { resolveBadgeStyle } from '@/widgets/FloorOccupancyWidget/badgeStyle';
import type { WidgetProps } from '@/widgets/registry';
import { resolveWidgetTopic } from '@/widgets/widgetTopic';

/**
 * Meeting rooms in columns, each row a room name and a colour-coded comfort badge. Port of
 * `floorOccupancy/floorOccupancyWidget.vue`.
 *
 * Used by three deployed configs (`szeged_2`, `szeged_3`, `szeged_4`), each with a single
 * `Floor 1` column of two to six rooms and the same three gradient badge styles.
 *
 * Despite living in the "chart widgets" ticket it draws no chart — it is here because it
 * shares the title/text-block header with the two that do.
 *
 * ## Its data is not in the signage tree
 *
 * Every other widget reads `<baseTopic>/<options.topic>`. This one's data is published to the
 * **tenant** topic `B0004/T0000003/environment-statuses` — one level above
 * `baseTopic` (`B0004/T0000003/signage`) — as a single array of every area in the building,
 * across every floor. What the signage tree carries at `<floor>/environment-statuses` is a
 * single area object, which is neither the right shape nor the right rooms.
 *
 * That topic is produced by a Node-RED flow shared with other teams' products and is not ours
 * to change, so the widget reaches out to it instead: `options.absoluteTopic`, when present,
 * is used verbatim and wins over `options.topic` (docs/MIGRATION.md decision #26). The three
 * deployed configs keep their original `topic` value untouched next to it — it is inert, and
 * removing a key from a file that is live on a display buys nothing.
 *
 * The array is not filtered per floor and does not need to be: the join is on `areacode`, so
 * a screen picks its own rooms out of the whole building and ignores the rest.
 */

/** One configured room. `areacode` is the join key against the payload. */
const roomSchema = z.object({
  areacode: z.string(),
  /** Fallback label; the payload's `areaname` wins when there is one. */
  name: z.string().default(''),
});

const badgeStyleSchema = z.object({
  value: z.string(),
  condition: z.string(),
  /** Raw CSS declaration string. */
  style: z.string().default(''),
});

const optionsSchema = z.object({
  topic: z.string().optional(),
  /**
   * A full broker topic, used verbatim in place of `topic`. See the note on the tenant tree
   * in this file's header and docs/MIGRATION.md decision #26.
   */
  absoluteTopic: z.string().optional(),
  layout: widgetLayoutSchema.optional(),
  mainTitle: z.string().optional(),
  mainTitleFont: fontOptionsSchema.optional(),
  title: z.string().optional(),
  titleFont: fontOptionsSchema.optional(),
  textBloc: z.object({ text: z.string().default('') }).prefault({}),
  textBlocFont: fontOptionsSchema.optional(),
  columns: z
    .array(z.object({ title: z.string().default(''), rooms: z.array(roomSchema).prefault([]) }))
    .prefault([]),
  badgeStyles: z.array(badgeStyleSchema).prefault([]),
  /** Raw CSS declaration string, applied to every room row. */
  roomStyle: z.string().optional(),
  roomColumnTitleFont: fontOptionsSchema.optional(),
});

type FloorOccupancyOptions = z.infer<typeof optionsSchema>;

/**
 * docs/MIGRATION-WIDGETS.md §7. `value` is deliberately `unknown`: a real deployment publishes
 * a dozen parameters per area and only `M076` is read, so one unexpected value type must not
 * reject the whole message.
 */
const payloadSchema = z.array(
  z.looseObject({
    areacode: z.string(),
    areaname: z.string().optional(),
    parameters: z.array(z.looseObject({ code: z.string(), value: z.unknown() })).prefault([]),
  }),
);

type AreaStatus = z.infer<typeof payloadSchema>[number];

/** The comfort index parameter, and what a room shows when it has none. */
const COMFORT_INDEX_CODE = 'M076';
const NO_VALUE = 'N/A';

/** Legacy `.room-column span.room-badge`. See the note on `badgeStyle` below. */
const BADGE_TRANSITION: CSSProperties = { transition: 'all 0.4s ease' };

export function FloorOccupancyWidget({ componentId, options }: WidgetProps) {
  const parsed = optionsSchema.safeParse(options);
  // Hooks run before any early return.
  const opts = parsed.success ? parsed.data : undefined;
  const { topic, absolute } = resolveWidgetTopic(opts ?? {});
  const data = useWidgetData<unknown>(componentId, topic, undefined, { absolute });
  const { className, style } = useWidgetLayout(opts?.layout);

  if (!parsed.success) {
    warnOnce(
      `floor-occupancy-options:${componentId}`,
      '[signage] FloorOccupancyWidget options rejected; rendering nothing. %s',
      z.prettifyError(parsed.error),
    );
    return null;
  }

  const opt = parsed.data;
  const areas = readPayload(data, componentId);
  warnOnMissingRooms(opt, areas, componentId, data !== undefined);
  const columns = buildColumns(opt, areas);

  const roomStyle = parseStyleString(opt.roomStyle);
  const columnTitleStyle = fontStyleToCss(opt.roomColumnTitleFont, { fontSize: '1.5rem' });

  const rightColumnClass = collapsesTitleColumn(opt.title, opt.textBloc.text)
    ? 'w-full pt-2 lg:pt-0'
    : 'w-full pt-2 lg:flex-1 lg:pt-0';

  return (
    <div className={className} style={style}>
      <MainTitle text={opt.mainTitle ?? ''} font={opt.mainTitleFont} />
      <TitleColumn className="w-full lg:flex-1" title={opt.title} titleFont={opt.titleFont}>
        <TextBloc text={resolveText(opt)} font={opt.textBlocFont} />
      </TitleColumn>
      <div className={rightColumnClass}>
        {columns.map((column, columnIndex) => (
          <div key={columnIndex} className="flex-1">
            {column.title ? (
              <div
                className="column-title flex w-full justify-center px-2 py-2 border-right"
                style={columnTitleStyle}
              >
                <h2>{column.title}</h2>
              </div>
            ) : null}
            {column.rooms.map((room, roomIndex) => (
              <div
                key={roomIndex}
                className="flex w-full justify-between items-center px-2 py-2 border-right"
                style={roomStyle}
              >
                <span className="inline-flex">{room.name}</span>
                <span
                  className="room-badge inline-flex items-center justify-center px-2 py-2 rounded"
                  style={{ ...BADGE_TRANSITION, ...parseStyleString(room.style) }}
                >
                  {room.value}
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * The payload as an array, or `[]`.
 *
 * **W-2, fixed.** The legacy widget called `data.value.find(...)` while its Vuex module
 * initialised `data` to `{}`, so `.find is not a function` was thrown on the very first render
 * of every one of the three displays using this widget — inside a `computed`, which is why it
 * degraded to an empty widget rather than a white screen. Defaulting to `[]` is what makes the
 * documented empty state (every room `N/A`) actually render.
 */
function readPayload(data: unknown, componentId: string): AreaStatus[] {
  if (data === undefined) return [];

  const payload = payloadSchema.safeParse(data);
  if (!payload.success) {
    warnOnce(
      `floor-occupancy-payload:${componentId}`,
      '[signage] FloorOccupancyWidget payload is not an array of area statuses; ' +
        'rendering every room as %s. %s',
      NO_VALUE,
      z.prettifyError(payload.error),
    );
    return [];
  }
  return payload.data;
}

/**
 * Warns once for the configured rooms the payload has no comfort index for.
 *
 * A room the payload never mentions and a room whose area reports every parameter except
 * `M076` both render `N/A`, and so does a room whose `areacode` was mistyped in the config.
 * On a wall-mounted display those three are indistinguishable, and the first is an ops
 * problem, the second a sensor problem and the third ours — so the console says which rooms
 * are affected and the reader can go and look at the area in the payload.
 *
 * Only after a message has arrived: before that, every room is legitimately without data and
 * the warning would fire on every display at boot.
 */
function warnOnMissingRooms(
  opt: FloorOccupancyOptions,
  areas: AreaStatus[],
  componentId: string,
  hasData: boolean,
): void {
  if (!hasData) return;

  const missing = opt.columns
    .flatMap((column) => column.rooms)
    .filter((room) => {
      const area = areas.find((candidate) => candidate.areacode === room.areacode);
      return area === undefined || comfortIndexOf(area) === NO_VALUE;
    })
    .map((room) => `${room.areacode}${room.name ? ` (${room.name})` : ''}`);

  if (missing.length === 0) return;

  warnOnce(
    `floor-occupancy-missing:${componentId}`,
    '[signage] FloorOccupancyWidget: no %s reading for %d configured room(s), rendering ' +
      'them as %s — %s. The payload carried %d area(s).',
    COMFORT_INDEX_CODE,
    missing.length,
    NO_VALUE,
    missing.join(', '),
    areas.length,
  );
}

type RenderedRoom = { name: string; value: string; style: string };
type RenderedColumn = { title: string; rooms: RenderedRoom[] };

/** The configured columns joined to the payload by `areacode`. */
function buildColumns(opt: FloorOccupancyOptions, areas: AreaStatus[]): RenderedColumn[] {
  return opt.columns.map((column) => ({
    title: column.title,
    rooms: column.rooms.map((room) => {
      const areaData = areas.find((area) => area.areacode === room.areacode);
      const comfortIndex = areaData ? comfortIndexOf(areaData) : NO_VALUE;
      return {
        // Legacy took `roomData.areaname` unguarded, so an area present in the payload but
        // without a name rendered the literal `undefined`. The configured name is the fallback.
        name: areaData?.areaname ?? room.name,
        value: comfortIndex,
        style: resolveBadgeStyle(opt.badgeStyles, comfortIndex),
      };
    }),
  }));
}

/** The `M076` parameter's value, or `N/A` when the area does not report one. */
function comfortIndexOf(area: AreaStatus): string {
  for (const parameter of area.parameters) {
    if (parameter.code === COMFORT_INDEX_CODE) {
      const { value } = parameter;
      // Legacy returned the raw value and interpolated it into a string either way. Anything
      // that is not a scalar would render as `[object Object]`, which reads as a broken badge
      // where `N/A` reads as a missing reading.
      if (typeof value === 'string' || typeof value === 'number') return String(value);
      return NO_VALUE;
    }
  }
  return NO_VALUE;
}

/**
 * The text block. **Always the `'strip'`-mode placeholder** — every `{{variable}}` rendered as
 * `0`, braces stripped (docs/MIGRATION-WIDGETS.md §3).
 *
 * That is not a shortcut, it is the legacy behaviour: `floorOccupancyWidget.vue` seeds
 * `textBloc` with the placeholder and then never writes to it again. It has no `watch` on the
 * payload at all — only `areaValues` is reactive — and its payload is an array of area
 * statuses rather than a flat variable map, so there would be nothing to substitute *from*.
 * All three deployed configs carry `"textBloc": { "text": "" }`, so this renders nothing on
 * every display today.
 */
function resolveText(opt: FloorOccupancyOptions): string {
  return placeholderText(opt.textBloc.text);
}
